/**
 * Equa Gallery — small vanilla behaviours that the Horizon runtime does not provide.
 *
 * - <equa-rail>: horizontal scroll rail with previous/next buttons (scrolls by one card).
 * - <equa-header>: adds `data-scrolled` when the page scrolls past the hero top, and keeps the
 *   mobile menu trigger's aria-expanded in sync with the theme-drawer events.
 * - <equa-signup-form>: client-side e-mail validation before the Shopify customer form posts.
 * - <equa-sheet>: a [data-sheet-open] button and the bottom-sheet <dialog> it opens (the menu
 *   drawer's language and currency). Escape and backdrop taps close only the sheet, never the
 *   drawer around it; the head ([data-sheet-drag]) drags the sheet down to dismiss it.
 * - <equa-buy-now>: the product page's Buy now button. Adds the product form's selection with
 *   /cart/add.js, then posts the cart form with `checkout` (the cart drawer's route to checkout).
 *   A plain button, not a submit: the Horizon product form turns every submit into an add to
 *   cart that opens the drawer. Hidden while the selected variant is unavailable.
 * - <equa-read-more>: a [data-read-more-content] block clamped by CSS and the
 *   [data-read-more-toggle] button that expands it (aria-expanded drives the CSS). Hides the
 *   button and the fade while the text fits. State lives on the children, so the host carries
 *   data-skip-subtree-update and a facet re-render does not collapse it.
 */

class EquaRail extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-rail]');
    if (!this.scroller) return;

    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');

    this.onPrev = () => this.scrollBy(-1);
    this.onNext = () => this.scrollBy(1);
    this.onScroll = () => this.updateButtons();

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(this.onScroll);
    this.resizeObserver.observe(this.scroller);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  get step() {
    const first = this.scroller.firstElementChild;
    if (!first) return this.scroller.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(this.scroller).columnGap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  scrollBy(direction) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scroller.scrollBy({ left: this.step * direction, behavior: reduce ? 'auto' : 'smooth' });
  }

  updateButtons() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
    this.prev?.toggleAttribute('disabled', atStart);
    this.next?.toggleAttribute('disabled', atEnd);
    this.toggleAttribute('data-scrollable', scrollWidth > clientWidth + 2);
  }
}

class EquaHeader extends HTMLElement {
  connectedCallback() {
    this.onScroll = () => this.updateScrolled();
    this.scroller = this.closest('.page-wrapper') || document;
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.updateScrolled();

    this.menuId = this.dataset.menuDrawer;
    if (this.menuId) {
      this.onDrawer = (event) => {
        const drawer = event.target instanceof Element ? event.target.closest('theme-drawer') : null;
        if (!drawer || drawer.id !== this.menuId) return;
        const open = event.type === 'theme-drawer:open';
        this.querySelectorAll(`[aria-controls="${this.menuId}"]`).forEach((button) => {
          button.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      };
      document.addEventListener('theme-drawer:open', this.onDrawer);
      document.addEventListener('theme-drawer:close', this.onDrawer);
    }

    // Shopify's <shopify-account> sheet opens at the top right of the viewport by default; anchor it
    // under the account button instead. Its "open" event is composed but does not bubble: capture.
    this.onAccountOpen = (event) => {
      const account = event.target;
      if (!(account instanceof HTMLElement) || account.localName !== 'shopify-account') return;
      const place = () => {
        const rect = account.getBoundingClientRect();
        // The sheet is position: fixed, so measure against the html box (the viewport minus any
        // scrollbar or gutter), not clientWidth, which ignores a scrollbar gutter.
        const right = document.documentElement.getBoundingClientRect().width - rect.right;
        account.style.setProperty('--eq-account-top', `${Math.max(0, Math.round(rect.bottom))}px`);
        account.style.setProperty('--eq-account-right', `${Math.max(0, Math.round(right))}px`);
      };
      place();
      requestAnimationFrame(place); // the component's scroll lock can reflow the page after "open"
    };
    this.addEventListener('open', this.onAccountOpen, true);
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.onScroll);
    if (this.onDrawer) {
      document.removeEventListener('theme-drawer:open', this.onDrawer);
      document.removeEventListener('theme-drawer:close', this.onDrawer);
    }
    this.removeEventListener('open', this.onAccountOpen, true);
  }

  updateScrolled() {
    const top = this.scroller === document ? window.scrollY : this.scroller.scrollTop;
    this.toggleAttribute('data-scrolled', top > 24);
  }
}

class EquaSignupForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.error = this.querySelector('[data-signup-error]');
    if (!this.form || !this.input) return;
    this.onSubmit = (event) => {
      const value = this.input.value.trim();
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
      if (valid) {
        this.form.classList.add('is-submitting');
        return;
      }
      event.preventDefault();
      this.input.setAttribute('aria-invalid', 'true');
      this.error?.removeAttribute('hidden');
      this.input.focus();
    };
    this.onInput = () => {
      this.input.removeAttribute('aria-invalid');
      this.error?.setAttribute('hidden', '');
    };
    this.form.addEventListener('submit', this.onSubmit);
    this.input.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.input?.removeEventListener('input', this.onInput);
  }
}

class EquaSheet extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-sheet-open]');
    this.handle = this.querySelector('[data-sheet-drag]');
    if (!this.dialog || !this.trigger) return;

    this.onOpen = () => this.open();
    this.onClick = (event) => {
      // The inner wrapper fills the dialog box, so a click that targets the dialog itself landed
      // on its ::backdrop. Stop it here: theme-drawer closes the drawer on outside clicks.
      if (event.target === this.dialog) {
        event.stopPropagation();
        this.close();
      } else if (event.target instanceof Element && event.target.closest('[data-sheet-close]')) {
        this.close();
      }
    };
    // Capture on document so this runs before theme-drawer's Escape handler, wherever focus is;
    // theme-drawer skips events that are already defaultPrevented.
    this.onKeyDown = (event) => {
      if (event.key !== 'Escape' || event.isComposing || !this.dialog.open) return;
      event.preventDefault();
      this.close();
    };
    this.onClosed = () => {
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeyDown, true);
      this.drag = null;
      this.dialog.classList.remove('is-dragging');
      this.dialog.style.removeProperty('translate');
      // The dialog restores focus to whatever had it before, which is not the trigger when a tap
      // did not focus it (Safari); skip when the drawer itself is closing.
      if (this.closest('dialog')?.open) this.trigger.focus({ preventScroll: true });
    };
    this.onDrawerClose = (event) => {
      if (this.dialog.open && event.target instanceof Node && event.target.contains(this)) this.close();
    };
    // The tapped option looks selected while the localization form reloads the page
    this.onSubmit = (event) => {
      event.submitter?.setAttribute('data-pending', '');
    };
    this.onPageShow = (event) => {
      if (event.persisted) this.querySelectorAll('[data-pending]').forEach((el) => el.removeAttribute('data-pending'));
    };

    this.trigger.addEventListener('click', this.onOpen);
    this.dialog.addEventListener('click', this.onClick);
    this.dialog.addEventListener('close', this.onClosed);
    this.dialog.addEventListener('submit', this.onSubmit);
    document.addEventListener('theme-drawer:close', this.onDrawerClose);
    window.addEventListener('pageshow', this.onPageShow);

    if (this.handle) {
      this.onPointerDown = (event) => this.dragStart(event);
      this.onPointerMove = (event) => this.dragMove(event);
      this.onPointerUp = (event) => this.dragEnd(event, false);
      this.onPointerCancel = (event) => this.dragEnd(event, true);
      this.handle.addEventListener('pointerdown', this.onPointerDown);
      this.handle.addEventListener('pointermove', this.onPointerMove);
      this.handle.addEventListener('pointerup', this.onPointerUp);
      this.handle.addEventListener('pointercancel', this.onPointerCancel);
    }
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onOpen);
    this.dialog?.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('close', this.onClosed);
    this.dialog?.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('theme-drawer:close', this.onDrawerClose);
    document.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('pageshow', this.onPageShow);
    this.handle?.removeEventListener('pointerdown', this.onPointerDown);
    this.handle?.removeEventListener('pointermove', this.onPointerMove);
    this.handle?.removeEventListener('pointerup', this.onPointerUp);
    this.handle?.removeEventListener('pointercancel', this.onPointerCancel);
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onKeyDown, true);
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  dragStart(event) {
    if (event.button !== 0 || (event.target instanceof Element && event.target.closest('button'))) return;
    this.drag = { id: event.pointerId, startY: event.clientY, lastY: event.clientY, lastT: event.timeStamp, speed: 0 };
    this.handle.setPointerCapture(event.pointerId);
    this.dialog.classList.add('is-dragging');
  }

  dragMove(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dt = event.timeStamp - drag.lastT;
    if (dt > 0) drag.speed = (event.clientY - drag.lastY) / dt;
    drag.lastY = event.clientY;
    drag.lastT = event.timeStamp;
    this.dialog.style.translate = `0 ${Math.max(0, event.clientY - drag.startY)}px`;
  }

  dragEnd(event, cancelled) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.drag = null;
    const distance = event.clientY - drag.startY;
    const dismiss = !cancelled && (distance > Math.min(140, this.dialog.offsetHeight * 0.3) || drag.speed > 0.6);
    // Same frame: the CSS transition takes over from the dragged position, down to closed or back up
    this.dialog.classList.remove('is-dragging');
    this.dialog.style.removeProperty('translate');
    if (dismiss) this.close();
  }
}

class EquaBuyNow extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-buy-now]');
    this.error = this.querySelector('[data-buy-now-error]');
    this.section = this.closest('.shopify-section');
    if (!this.button) return;
    this.busy = false;
    this.pending = null;
    this.onClick = () => this.buy();
    // Same event the product form listens to; its promise settles once the variant resolves
    this.onSelect = (event) => this.track(event.promise);
    // Coming back from checkout restores the page from the back/forward cache mid-request
    this.onPageShow = (event) => {
      if (event.persisted) this.setBusy(false);
    };
    this.button.addEventListener('click', this.onClick);
    this.section?.addEventListener('shopify:product:select', this.onSelect);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.section?.removeEventListener('shopify:product:select', this.onSelect);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  track(promise) {
    if (!promise) return;
    const pending = Promise.resolve(promise)
      .then((result) => {
        const detail = result?.detail;
        if (this.pending === pending && detail?.html) this.hidden = detail.resource?.available !== true;
      })
      .catch(() => {})
      .finally(() => {
        if (this.pending === pending) this.pending = null;
      });
    this.pending = pending;
  }

  async buy() {
    const form = this.closest('form');
    if (this.busy || !form) return;
    this.setBusy(true);
    this.showError('');
    try {
      if (this.pending) {
        await this.pending;
        // One more task so the product form has written the resolved variant id into the form
        await new Promise((resolve) => setTimeout(resolve));
      }
      if (this.hidden) {
        this.setBusy(false);
        return;
      }
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) throw new Error(data.description || data.message || '');
      this.checkout();
    } catch (error) {
      this.setBusy(false);
      this.showError(error.message || this.dataset.error);
    }
  }

  checkout() {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = this.dataset.cartUrl;
    form.hidden = true;
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = 'checkout';
    form.append(field);
    document.body.append(form);
    form.submit();
  }

  setBusy(busy) {
    this.busy = busy;
    this.button.setAttribute('aria-busy', String(busy));
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }
}

class EquaReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.content || !this.toggle) return;
    this.onClick = () => {
      const open = this.toggle.getAttribute('aria-expanded') !== 'true';
      this.toggle.setAttribute('aria-expanded', String(open));
    };
    this.toggle.addEventListener('click', this.onClick);
    // Re-measure on width changes only: the height animates while opening and closing.
    this.width = 0;
    this.observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === this.width) return;
      this.width = entry.contentRect.width;
      this.measure();
    });
    this.observer.observe(this.content);
    document.fonts?.ready.then(() => this.measure());
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  measure() {
    if (!this.isConnected) return;
    if (this.toggle.getAttribute('aria-expanded') === 'true') return;
    const fits = this.content.scrollHeight <= this.content.clientHeight + 1;
    this.content.toggleAttribute('data-fits', fits);
    this.toggle.hidden = fits;
  }
}

if (!customElements.get('equa-rail')) customElements.define('equa-rail', EquaRail);
if (!customElements.get('equa-header')) customElements.define('equa-header', EquaHeader);
if (!customElements.get('equa-signup-form')) customElements.define('equa-signup-form', EquaSignupForm);
if (!customElements.get('equa-sheet')) customElements.define('equa-sheet', EquaSheet);
if (!customElements.get('equa-buy-now')) customElements.define('equa-buy-now', EquaBuyNow);
if (!customElements.get('equa-read-more')) customElements.define('equa-read-more', EquaReadMore);
